import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export function Combobox({
  options,
  value,
  setValue,
  placeholder,
  searchBoxPlaceholder,
  buttonClassName,
  searchBoxFallback,
}: {
  options: { value: string; label: string }[];
  value: string;
  setValue: (value: string) => void;
  placeholder?: string;
  buttonClassName?: string;
  searchBoxPlaceholder?: string;
  searchBoxFallback?: string;
}) {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline2"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-[250px] justify-between overflow-hidden text-ellipsis whitespace-nowrap",
            buttonClassName,
          )}
          title={options.find((option) => option.value === value)?.label}
        >
          <span className="block min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-left">
            {value
              ? (() => {
                  const label = options.find(
                    (option) => option.value === value,
                  )?.label;
                  if (!label) return "";
                  return label;
                })()
              : (placeholder ?? "Select option")}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command className="light">
          <CommandInput
            placeholder={searchBoxPlaceholder ?? "Search options..."}
          />
          <CommandList>
            <CommandEmpty>
              {searchBoxFallback ?? "No options found."}
            </CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={(currentValue) => {
                    const value = options.find(
                      (option) => option.label === currentValue,
                    )?.value;
                    setValue(value ?? "");
                    setOpen(false);
                  }}
                  title={option.label}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="block min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                    {option.label}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
